import { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useMetaAds, useGoogleAds } from "hooks"
import { RefreshCw, ChevronLeft } from "lucide-react"
import { CampaignDetailsTab, CampaignDetailsAdsTab, CampaignDetailsHeader } from "components"
import { googleApi, metaApi } from "api"
import toast from "react-hot-toast"
import MobileTopNavBar from 'components/MobileTopNavBar';

const CampaignDetailsPage = () => {
  const { id, platform } = useParams()
  const navigate = useNavigate()
  const { fetchCampaignById: fetchMetaCampaignById } = useMetaAds()
  const { fetchCampaignById: fetchGoogleCampaignById } = useGoogleAds()
  const [campaign, setCampaign] = useState(null)
  const [loading, setLoading] = useState(true)
  const [statusUpdating, setStatusUpdating] = useState(false)
  const [saveUpdating, setSaveUpdating] = useState(false)
  const [activeTab, setActiveTab] = useState("details")
  const [editMode, setEditMode] = useState(false)
  const [editedData, setEditedData] = useState({})

  const apiUpdateMap = {
    meta: metaApi.updateMetaAdsCampaign,
    google: googleApi.updateGoogleAdsCampaign,
  }

  const loadCampaign = useCallback(async (force) => {
    try {
      setLoading(true)
      const fetchCampaignById = platform === "meta" ? fetchMetaCampaignById : fetchGoogleCampaignById
      if (!fetchCampaignById) throw new Error("Plataforma no soportada")
      const data = await fetchCampaignById(id, { force })
      if (!data) throw new Error(data?.error || "Hubo un error al cargar la campaña")
      setCampaign(data)
    } catch (err) {
      toast.error(err?.message || "Error al cargar la campaña")
      navigate('/campaigns')
    } finally {
      setLoading(false)
    }
  }, [platform, fetchMetaCampaignById, fetchGoogleCampaignById, id, navigate])

  const updateCampaignFields = async (fields) => {
    const updateFn = apiUpdateMap[platform]
    if (!updateFn) throw new Error("Plataforma no soportada para actualización")

    setSaveUpdating(true)

    try {
      const updatedCampaign = await updateFn(campaign.provider_campaign_id, fields)
      toast.success("Campaña actualizada correctamente")
      
      setCampaign(updatedCampaign)
      
      return true
    } catch ({ response: err }) {
      const message = err?.data?.error || "Error al actualizar la campaña"
      toast.error(message)
      return false
    } finally {
      setStatusUpdating(false)
      setSaveUpdating(false)
    }
  }

  const handleFieldChange = async (field, value) => {
    const isStatus = field === "status"
    isStatus ? setStatusUpdating(true) : setSaveUpdating(true)

    await updateCampaignFields({ payload: { [field]: value } })

    isStatus ? setStatusUpdating(false) : setSaveUpdating(false)
  }

  const handleDataChange = (data) => {
    setEditedData((prev) => ({ ...prev, ...data }))
  }

  const handleSave = async () => {
    if (!Object.keys(editedData).length) return

    setSaveUpdating(true)
    const success = await updateCampaignFields(editedData)
    if (success) {
      setEditedData({})
      setEditMode(false)
    }
    setSaveUpdating(false)
  }

  const handleTabChange = (tab) => {
    if (editMode && tab !== "details") {
      setEditMode(false)
      setEditedData({})
    }
    setActiveTab(tab)
  }

  const handleSetEditMode = (mode) => {
    setEditMode(mode)
    if (mode) {
      setActiveTab("details")
    }
  }

  useEffect(() => {
    loadCampaign()
  }, [loadCampaign])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <RefreshCw className="h-6 w-6 animate-spin mb-3 text-white" />
          <span className="text-white/70">Cargando campaña...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-6">
      <MobileTopNavBar hideBack={false} sticky={true} title="Detalle de Campaña" transparent={true} hideBrandSelector={true} />
      
      <div className="max-w-7xl mx-auto">
        {/* Botón volver - solo en desktop */}
        <button
          onClick={() => navigate("/campaigns")}
          className="hidden md:flex items-center text-white/70 hover:text-white transition mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          <span>Volver a campañas</span>
        </button>

        <CampaignDetailsHeader
          campaign={campaign}
          platform={platform}
          onStatusChangeRequest={(newStatus) => handleFieldChange("status", newStatus)}
          statusLoading={statusUpdating}
          saveLoading={saveUpdating}
          onSetEditMode={handleSetEditMode}
          editMode={editMode}
          onSave={handleSave}
          onClose={() => {
            setEditMode(false)
            setEditedData({})
          }}
        />
    
        {/* Tabs con estilo dark */}
        <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-xl p-1 mb-6">
          <div className="flex space-x-1">
            <button
              onClick={() => handleTabChange("details")}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "details"
                  ? "bg-white/15 border border-white/30 text-white"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              Detalles
            </button>
            <button
              onClick={() => handleTabChange("ads")}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "ads"
                  ? "bg-white/15 border border-white/30 text-white"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
            >
              Anuncios
            </button>
          </div>
        </div>
    
        {/* Contenido de las tabs */}
        <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-xl p-6">
          {activeTab === "details" && (
            <CampaignDetailsTab
              data={campaign}
              platform={platform}
              editMode={editMode}
              onDataChange={handleDataChange}
              loading={saveUpdating}
            />
          )}
          {activeTab === "ads" && <CampaignDetailsAdsTab platform={platform} data={campaign} />}
        </div>
      </div>
    </div>
  )
  
}

export default CampaignDetailsPage
